import { useState, useContext, useEffect, useMemo } from 'react'
import _ from "lodash"

import { TextField, Typography, Tooltip, Button, FormControlLabel, FormLabel, RadioGroup, Radio } from '@mui/material'
import EastRoundedIcon from '@mui/icons-material/EastRounded'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'

import { AppContext } from '/providers/app-context'
import { PassContext } from '../pass-context'
import dataSelector from './data-selector'
import PopupShell from '../popup-shell'
import { validateRequiredVariableValue } from '/utils/variable-required'

const PopupDialogText = ({ open, close }) => {

  const { client } = useContext(AppContext)
  const { template, setTemplate, variables, setVariables, variablesCatalog = [], isManagedPassTemplate = false } = useContext(PassContext)

  const [newVariables, setNewVariables] = useState({})
  const [barcode, setBarcode] = useState()
  const [readonly, setReadonly] = useState(false)
  const catalogMap = _.keyBy(variablesCatalog, "var_name")
  const isAdminUser = Number(client?.client_id) === 0
  const getCatalogDefault = path => catalogMap[path]?.default_value ?? ""

  const barcodeFormat = useMemo(() => {
    return dataSelector({ template, variables: newVariables, path: "barcode.format" }) ?? "NONE"
  },[newVariables])

  const payloadRequiredValidation = useMemo(() => {
    if (barcodeFormat === "NONE") return { valid: true, helperText: "" }
    return validateRequiredVariableValue({
      value: _.get(newVariables, "barcode.payload"),
      catalogEntry: catalogMap["barcode.payload"],
      label: "Barcode payload",
      isManagedPassTemplate
    })
  }, [barcodeFormat, catalogMap, isManagedPassTemplate, newVariables])
  
  const textRequiredValidation = useMemo(() => {
    if (barcodeFormat === "NONE") return { valid: true, helperText: "" }
    return validateRequiredVariableValue({
      value: _.get(newVariables, "barcode.text"),
      catalogEntry: catalogMap["barcode.text"],
      label: "Barcode text",
      isManagedPassTemplate
    })
  }, [barcodeFormat, catalogMap, isManagedPassTemplate, newVariables])

  // Runs on "open" state change or if template / instance data change 
  // Ensures we have the current data rather than something left behind from previous form 
  useEffect(() => {
    try {
      if (open) {
        setNewVariables(_.cloneDeep(variables))
        setReadonly(false)

        if (template.barcode) {
          const initialBarcode = _.get(template, "barcode")
          setBarcode(initialBarcode)
          if (initialBarcode.readonly) setReadonly(true)
        }
      }
    }
    catch (err) {
      throw err
    }
  }, [open, template, variables])

  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Support Functions
  //////////////////////////////////////////////////////////////////////////////////////////////////////////

  const saveFormData = event => {
    event.preventDefault() // Prevents a "submit" which reloads the page

    // Save content of our "new" template/instance data back to state
    if (!_.isEqual(newVariables, variables)) setVariables(newVariables)
    if (!_.isEqual(readonly, template.barcode.readonly ?? false)) {

      let nt = _.cloneDeep(template)

      if (readonly) _.set(nt,"barcode.readonly",true)
      else _.unset(nt,"barcode.readonly")
      setTemplate(nt)
    }
    close()
  }

  const handleChangeReadonly = enabled => {
    setReadonly(enabled)
  }

  const instanceFields = {
    message : "barcode.payload",
    altText : "barcode.text",
    format : "barcode.format"
  }

  const fieldReadonly = field => {
    const catalogEntry = catalogMap[instanceFields[field]]
    return catalogEntry?.editable !== undefined && Number(catalogEntry?.editable) === 0
  }

  const barcodeReadonly = readonly || fieldReadonly("message") || fieldReadonly("altText")

  const updateBarcode = (field, value) => {
    if (fieldReadonly(field)) return

    setNewVariables((prev) => { 
      let t = _.cloneDeep(prev)

      if (field === "format") {
        if (value === "NONE") {
          _.unset(t, "barcode.payload")
          _.set(t, "barcode.format", "NONE")
          _.unset(t, "barcode.text")
        } else {
          if (barcodeFormat === "NONE") {
            _.set(t, "barcode.payload", getCatalogDefault("barcode.payload"))
            _.set(t, "barcode.text", getCatalogDefault("barcode.text"))
          }
          _.set(t, "barcode.format", value)
        }
        return t
      }

      _.set(t, instanceFields[field], value)
      return t
    })
  }

  const handleClose = (e, reason) => {
    if (reason && reason === "backdropClick")
      return
    close()
  }


  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Main Render
  //////////////////////////////////////////////////////////////////////////////////////////////////////////

  try {
    return (
      <PopupShell open={open} onClose={handleClose}>

        <div className="flex flex-col p-2 h-full w-full overflow-x-hidden">

          <div className="flex flex-row justify-between mt-5 w-full">
            <Typography color="secondary" variant="h6">Barcode</Typography>
            <Tooltip title="You can press ESC on your keyboard to dismiss"><Button endIcon={<EastRoundedIcon />} onClick={handleClose} >cancel</Button></Tooltip>
          </div>

          {barcodeReadonly &&
            <Typography variant="h5">
              <p>This barcode is read-only.</p>
              <p>The barcode type and content cannot be modified in the editor.</p>
            </Typography>
          }

          {isAdminUser && (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2 }}>
              <Typography>Barcode editable</Typography>
              <Switch checked={readonly} onChange={e => handleChangeReadonly(e.target.checked)} />
              <Typography>Barcode readonly</Typography>
            </Stack>
          )}

          <RadioGroup
            value={barcodeFormat}
            onChange={e => updateBarcode("format",e.target.value)}
          >
            <FormControlLabel value="NONE" control={<Radio disabled={barcodeReadonly} />} label="No barcode" />
            <FormControlLabel value="AZTEC" control={<Radio disabled={barcodeReadonly} />} label="Aztec" />
            <FormControlLabel value="QR_CODE" control={<Radio disabled={barcodeReadonly} />} label="QR Code" />
            <FormControlLabel value="PDF_417" control={<Radio disabled={barcodeReadonly} />} label="PDF 417" />
            <FormControlLabel value="CODE_128" control={<Radio disabled={barcodeReadonly} />} label="Code 128" />
          </RadioGroup>

          {barcodeFormat !== "NONE" && <>
            <div className='flex flex-col gap-2'>
              <FormLabel id="barcode_type_label">Barcode Payload</FormLabel>
              <DataSourceData barcode={barcode} newVariables={newVariables} field="message" updateBarcode={updateBarcode} readonly={barcodeReadonly || fieldReadonly("message")} requiredValidation={payloadRequiredValidation} />
            </div>

            <div className='flex flex-col gap-2'>
              <FormLabel id="barcode_type_label">Barcode Text</FormLabel>
              <DataSourceData barcode={barcode} newVariables={newVariables} field="altText" updateBarcode={updateBarcode} readonly={barcodeReadonly || fieldReadonly("altText")} requiredValidation={textRequiredValidation} />
            </div>
          </>}

          <div className='fixed bottom-5 w-[460px]'>

            <hr className='mb-5' />

            <div className="flex flex-row w-full justify-end">
              <Button variant="contained" onClick={saveFormData} disabled={!payloadRequiredValidation.valid || !textRequiredValidation.valid}>Save</Button>
            </div>
          </div>
        </div>
      </PopupShell >
    )
  }
  catch (err) {
    throw err
  }
}

export default PopupDialogText

//////////////////////////////////////////////////////////////////////////////////////////////////////////
// Controlled JSX (Must have values passes as paramets and not access them from the parent 
// - hence outside the main function). Otherwise they do not update when data changes!
//////////////////////////////////////////////////////////////////////////////////////////////////////////

const DataSourceData = ({ barcode, newVariables, field, updateBarcode, readonly, requiredValidation }) => {

  const resolvedInstanceValue = dataSelector({ template: barcode, variables: newVariables, path: field })

  return (
    <TextField
      value={barcode ? resolvedInstanceValue : ""}
      type="text"
      disabled={!barcode || readonly}
      error={!requiredValidation.valid}
      helperText={requiredValidation.helperText || undefined}
      onChange={(event) => { updateBarcode(field, event.target.value) }}
      multiline={field === "message"}
      maxRows={10}
      fullWidth
    />

  )
}
